/**
 * @file ProducersPage.tsx
 * @description Производители (phase08.5; phase19.31 — каркас справочника (б)
 * разбора B).
 *
 * Таблица от 1024: Название · Место · Товаров · Состояние; на телефоне —
 * компактный список «Название … 8 товаров / Место · Активен». Строка — ссылка
 * на карточку производителя (сертификаты). «⋯»: открыть, изменить,
 * включить/отключить, после линии — удалить (с подтверждением; если к
 * производителю привязаны товары, сервер не удаляет — предлагаем отключить).
 *
 * У производителя нет флага публикации — в колонке состояние «Активен /
 * Отключён» (поле `isActive`).
 */

import { Factory, FolderOpen, Pencil, Plus, Power, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";

import { ProducerEditDialog } from "../../../components/catalog/ProducerEditDialog";
import { useDirectoryList } from "../../../components/catalog/directory/useDirectoryList";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { ResponsiveList } from "../../../components/ui/ResponsiveList";
import type { ListColumn } from "../../../components/ui/responsive-list-types";
import type { RowAction } from "../../../components/ui/RowActionsMenu";
import { StatusPill } from "../../../components/ui/StatusPill";
import { useConfirm } from "../../../components/ui/use-confirm";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { catalogProducers, type CatalogProducer } from "../../../lib/api-catalog";
import { ApiError } from "../../../lib/api-client";
import { directoryActiveMeta } from "../../../lib/catalog-status";

const PRODUCER_COLUMNS: ListColumn<CatalogProducer>[] = [
  { key: "name", header: "Название", cell: (p) => <span className="font-medium">{p.displayName}</span>, compact: "title" },
  { key: "place", header: "Место", cell: (p) => <span className="text-on-surface-variant">{p.legalAddress || "—"}</span>, compact: "subtitle" },
  {
    key: "products",
    header: "Товаров",
    width: "w-[104px]",
    align: "end",
    cell: (p) => <span className="tabular-nums">{p.productsCount ?? 0}</span>,
    compact: "trailing",
    compactCell: (p) => <span className="tabular-nums">{p.productsCount ?? 0} тов.</span>,
  },
  { key: "state", header: "Состояние", width: "w-[140px]", cell: (p) => <StatusPill meta={directoryActiveMeta(p)} />, compact: "meta" },
];

export function ProducersPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const list = useDirectoryList(catalogProducers.list, "Не удалось загрузить производителей");
  const { confirm, request, settle } = useConfirm();
  const [dialog, setDialog] = useState<{ producer: CatalogProducer | null } | null>(null);
  const detailHref = (p: CatalogProducer) => `/shop/${slug}/catalog/producers/${p.id}`;

  async function run(work: () => Promise<unknown>, done: string, failed: string) {
    try {
      await work();
      notify.success(done);
      await list.refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : failed);
    }
  }

  async function remove(p: CatalogProducer) {
    const ok = await confirm({ title: `Удалить производителя «${p.displayName}»?`, action: "Удалить", danger: true });
    if (!ok) return;
    try {
      await catalogProducers.remove(p.id);
      notify.success("Производитель удалён");
      await list.refresh();
    } catch (err) {
      if (!(err instanceof ApiError && err.code === "producer_has_products")) {
        notify.error(err instanceof ApiError ? err.message : "Не удалось удалить производителя");
        return;
      }
      const count = (err.details as { productsCount?: number } | undefined)?.productsCount;
      const off = await confirm({
        title: "Удалить нельзя — есть товары",
        description: `К производителю привязаны товары${count ? ` (${count})` : ""}. Отключить его? Товары останутся, новых привязок не будет.`,
        action: "Отключить",
      });
      if (off) await run(() => catalogProducers.remove(p.id, true), "Производитель отключён", "Не удалось отключить");
    }
  }

  const rowActions = (p: CatalogProducer): RowAction[] => [
    { key: "open", label: "Открыть", icon: FolderOpen, run: () => navigate(detailHref(p)) },
    { key: "edit", label: "Изменить", icon: Pencil, run: () => setDialog({ producer: p }) },
    {
      key: "toggle",
      label: p.isActive ? "Отключить" : "Включить",
      icon: Power,
      run: () => void run(() => catalogProducers.update(p.id, { isActive: !p.isActive }), p.isActive ? "Производитель отключён" : "Производитель включён", "Не удалось изменить"),
    },
    { key: "delete", label: "Удалить", icon: Trash2, danger: true, run: () => void remove(p) },
  ];

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Производители"
        subtitle="Кто делает товар — показывается на карточке на сайте"
        action={
          <Button variant="brand" onClick={() => setDialog({ producer: null })}>
            <Plus className="size-4" />
            Производитель
          </Button>
        }
      />
      <ResponsiveList
        aria-label="Производители"
        rows={list.items}
        columns={PRODUCER_COLUMNS}
        rowKey={(p) => p.id}
        rowHref={detailHref}
        rowLabel={(p) => `Производитель ${p.displayName}`}
        rowActions={rowActions}
        rowActionsTitle={(p) => `Производитель «${p.displayName}»`}
        loading={list.loading}
        error={list.error}
        onRetry={() => void list.refresh()}
        empty={{ icon: Factory, title: "Производителей пока нет", description: "Добавьте первого — например, ваш собственный бренд. Производитель обязателен для публикации товара" }}
      />
      {dialog ? (
        <ProducerEditDialog
          open
          producer={dialog.producer}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            void list.refresh();
          }}
        />
      ) : null}
      <ConfirmDialog request={request} onSettle={settle} />
    </div>
  );
}
